import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { applyHead, headFor } from './head'

/**
 * Mounted once in the app shell. Every navigation rewrites the title,
 * description, canonical, robots and og/twitter tags from the registries.
 * The render window (`…/render`) sets its own live title afterwards, so this
 * runs first and does not fight it.
 *
 * Also guarantees one `h1` per artifact page: the instruments are canvases
 * with no heading of their own, so when the page draws none, a visually
 * hidden h1 carrying the artifact title is rendered (screen readers and the
 * accessibility tree see it; nothing changes on screen).
 */
export function HeadManager() {
  const { pathname } = useLocation()
  const spec = headFor(pathname)
  const [pageHasH1, setPageHasH1] = useState(true)

  useEffect(() => {
    applyHead(headFor(pathname))
  }, [pathname])

  useEffect(() => {
    const root = document.getElementById('root')
    if (!root) return
    let raf = 0
    const check = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() =>
        setPageHasH1(root.querySelector('h1:not([data-seo-h1])') !== null),
      )
    }
    check()
    // Pages are lazy-loaded, so the heading may arrive after the route changes.
    const mo = new MutationObserver(check)
    mo.observe(root, { childList: true, subtree: true })
    return () => {
      mo.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [pathname])

  if (!spec.h1 || pageHasH1) return null
  return (
    <h1 data-seo-h1 className="sr-only">
      {spec.h1}
    </h1>
  )
}
